import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRightLeft, Edit, Hash, MapPin, MoreHorizontal, Package, Save, Settings, ShieldCheck, Tag, Trash2, X } from 'lucide-react';

import { inventoryAPI, categoriesAPI, catalogsAPI, getErrorMessage, itemsAPI, settingsAPI } from '@/lib/api';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import DynamicSpecsForm from '@/components/DynamicSpecsForm';
import { ProductPosDescriptionConfigurator } from '@/components/inventory/ProductPosDescriptionConfigurator';
import SerialNumbersPanel from '@/components/inventory/SerialNumbersPanel';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { resolveProductSchemaKey } from '@/lib/productPosDescription';
import { resolveCatalogCategoryPrefix } from '@/lib/productCatalog';
import { cn } from '@/lib/utils';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'discontinued', label: 'Descontinuado' },
];

const sanitizeCatalogList = (entries = []) => entries.filter((entry) => entry && typeof entry === 'object');

export default function ProductDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const directEditRequested = searchParams.get('mode') === 'edit';
  const routeProductPreview = location.state?.productPreview || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(routeProductPreview);
  const [productCategories, setProductCategories] = useState([]);
  const [productSubcategories, setProductSubcategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [schemas, setSchemas] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [trackableItems, setTrackableItems] = useState([]);
  const [trackableStockByLocation, setTrackableStockByLocation] = useState({});
  const [serialNumbersDialogOpen, setSerialNumbersDialogOpen] = useState(false);
  const [resolveLocationDialogOpen, setResolveLocationDialogOpen] = useState(false);
  const [resolveLocationId, setResolveLocationId] = useState('');
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const safeProductCategories = useMemo(() => sanitizeCatalogList(productCategories), [productCategories]);
  const safeProductSubcategories = useMemo(() => sanitizeCatalogList(productSubcategories), [productSubcategories]);

  const findCategory = useCallback((categoryValue, categoryId = null, categoriesList = safeProductCategories) => {
    const safeCategoriesList = sanitizeCatalogList(categoriesList);
    if (categoryId) {
      const exact = safeCategoriesList.find((category) => category.id === categoryId);
      if (exact) return exact;
    }

    const normalized = String(categoryValue || '').trim().toLowerCase();
    const directMatch = safeCategoriesList.find((category) => (
      category.id === categoryValue
      || String(category.base_prefix || '').trim().toLowerCase() === normalized
      || String(category.name || '').trim().toLowerCase() === normalized
    )) || null;

    if (directMatch) return directMatch;

    const resolvedPrefix = resolveCatalogCategoryPrefix(categoryValue);
    if (resolvedPrefix) {
      return safeCategoriesList.find((category) => category.base_prefix === resolvedPrefix) || null;
    }

    return null;
  }, [safeProductCategories]);

  const findSubcategory = useCallback((value, subcategoryId = null, subcategoriesList = safeProductSubcategories) => {
    const safeSubcategoriesList = sanitizeCatalogList(subcategoriesList);
    if (subcategoryId) {
      const exact = safeSubcategoriesList.find((subcategory) => subcategory.id === subcategoryId);
      if (exact) return exact;
    }

    const normalized = String(value || '').trim().toLowerCase();
    return safeSubcategoriesList.find((subcategory) => (
      subcategory.id === value
      || String(subcategory.prefix || '').trim().toLowerCase() === normalized
      || String(subcategory.name || '').trim().toLowerCase() === normalized
    )) || null;
  }, [safeProductSubcategories]);

  const getSubcategoriesForCategory = useCallback((categoryValue, categoryId = null, categoriesList = safeProductCategories, subcategoriesList = safeProductSubcategories) => {
    const category = findCategory(categoryValue, categoryId, categoriesList);
    if (!category) return [];
    return sanitizeCatalogList(subcategoriesList).filter((subcategory) => subcategory.category_id === category.id);
  }, [findCategory, safeProductCategories, safeProductSubcategories]);

  const buildEditData = useCallback((currentProduct, categoriesList = safeProductCategories, subcategoriesList = safeProductSubcategories) => {
    const category = findCategory(currentProduct?.category, currentProduct?.category_id, categoriesList);
    const availableSubcategories = getSubcategoriesForCategory(category?.base_prefix || currentProduct?.category, category?.id, categoriesList, subcategoriesList);
    const subcategory = findSubcategory(currentProduct?.subcategory, currentProduct?.subcategory_id, subcategoriesList);
    const validSubcategory = subcategory && availableSubcategories.some((item) => item.id === subcategory.id) ? subcategory : null;

    return {
      name: currentProduct?.name || '',
      sku: currentProduct?.sku || '',
      brand: currentProduct?.brand || '',
      model: currentProduct?.model || '',
      category: category?.base_prefix || currentProduct?.category || '',
      category_id: category?.id || currentProduct?.category_id || null,
      subcategory: validSubcategory?.id || '',
      part_number: currentProduct?.part_number || '',
      status: currentProduct?.status || 'active',
      description: currentProduct?.description || '',
      specs: currentProduct?.technical_specs || currentProduct?.specs || {},
      pos_description_use_override: Boolean(currentProduct?.pos_description_use_override),
      pos_description_fields: currentProduct?.pos_description_fields || [],
      unit_price: currentProduct?.unit_price?.toString() || '',
      cost_price: currentProduct?.cost_price?.toString() || '',
      reorder_level: currentProduct?.reorder_level?.toString() || '0',
      invalidSubcategory: Boolean((currentProduct?.subcategory_id || currentProduct?.subcategory) && !validSubcategory),
    };
  }, [findCategory, findSubcategory, getSubcategoriesForCategory, safeProductCategories, safeProductSubcategories]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();
      const [productRes, categoriesRes, subcategoriesRes, schemasRes, auditRes, locationsRes] = await Promise.all([
        inventoryAPI.getProduct(productId),
        safeRequest(() => catalogsAPI.listProductCategories(), { data: [] }, { telemetry: { module: 'product-detail', endpoint: 'catalogs.product-categories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listProductSubcategories(null, true), { data: [] }, { telemetry: { module: 'product-detail', endpoint: 'catalogs.product-subcategories', onEvent: captureFetchTelemetry } }),
        safeRequest(() => categoriesAPI.getAllSchemas(), { data: {} }, { telemetry: { module: 'product-detail', endpoint: 'categories.schemas', onEvent: captureFetchTelemetry } }),
        safeRequest(() => inventoryAPI.getProductAudit(productId), { data: [] }, { telemetry: { module: 'product-detail', endpoint: 'inventory.product-audit', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'product-detail', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
      ]);

      const loadedProduct = productRes.data;
      const loadedCategories = sanitizeCatalogList(Array.isArray(categoriesRes) ? categoriesRes : categoriesRes.data || []);
      const loadedSubcategories = sanitizeCatalogList(Array.isArray(subcategoriesRes) ? subcategoriesRes : subcategoriesRes.data || []);
      const loadedLocations = sanitizeCatalogList(Array.isArray(locationsRes) ? locationsRes : locationsRes.data || []);
      const loadedSchemas = schemasRes.data || {};
      const loadedAudit = Array.isArray(auditRes) ? auditRes : auditRes.data || [];

      setProduct(loadedProduct);
      setProductCategories(loadedCategories);
      setProductSubcategories(loadedSubcategories);
      setLocations(loadedLocations);
      setSchemas(loadedSchemas);
      setAuditLogs(loadedAudit.slice(0, 8));

      const normalizedCategory = String(loadedProduct?.category || '').trim().toLowerCase();
      const category = loadedCategories.find((item) => item && (
        item.id === loadedProduct?.category_id
        || item.id === loadedProduct?.category
        || String(item.base_prefix || '').trim().toLowerCase() === normalizedCategory
        || String(item.name || '').trim().toLowerCase() === normalizedCategory
      )) || null;
      const availableSubcategories = loadedSubcategories.filter((item) => item && item.category_id === category?.id);
      const normalizedSubcategory = String(loadedProduct?.subcategory || '').trim().toLowerCase();
      const subcategory = loadedSubcategories.find((item) => item && (
        item.id === loadedProduct?.subcategory_id
        || item.id === loadedProduct?.subcategory
        || String(item.prefix || '').trim().toLowerCase() === normalizedSubcategory
        || String(item.name || '').trim().toLowerCase() === normalizedSubcategory
      )) || null;
      const validSubcategory = subcategory && availableSubcategories.some((item) => item.id === subcategory.id) ? subcategory : null;

      const nextEditData = {
        name: loadedProduct?.name || '',
        sku: loadedProduct?.sku || '',
        brand: loadedProduct?.brand || '',
        model: loadedProduct?.model || '',
        category: category?.base_prefix || loadedProduct?.category || '',
        category_id: category?.id || loadedProduct?.category_id || null,
        subcategory: validSubcategory?.id || '',
        part_number: loadedProduct?.part_number || '',
        status: loadedProduct?.status || 'active',
        description: loadedProduct?.description || '',
        specs: loadedProduct?.technical_specs || loadedProduct?.specs || {},
        pos_description_use_override: Boolean(loadedProduct?.pos_description_use_override),
        pos_description_fields: loadedProduct?.pos_description_fields || [],
        unit_price: loadedProduct?.unit_price?.toString() || '',
        cost_price: loadedProduct?.cost_price?.toString() || '',
        reorder_level: loadedProduct?.reorder_level?.toString() || '0',
        invalidSubcategory: Boolean((loadedProduct?.subcategory_id || loadedProduct?.subcategory) && !validSubcategory),
      };

      setEditData(nextEditData);
      setInitialSnapshot(JSON.stringify(nextEditData));
      if (directEditRequested) {
        setEditMode(true);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar detalhe do produto'));
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, directEditRequested, navigate, productId, resetFetchTelemetry]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadTrackableItems = useCallback(async () => {
    if (!product?.id || !product?.is_trackable) {
      setTrackableItems([]);
      setTrackableStockByLocation({});
      return;
    }

    try {
      const response = await itemsAPI.list({ product_id: product.id });
      const items = Array.isArray(response.data) ? response.data : [];
      setTrackableItems(items);

      const stockMap = {};
      items.forEach((item) => {
        if (item.state !== 'in_stock') return;
        const locName = item.location_name || item.location || 'Sem localização';
        if (!stockMap[locName]) {
          stockMap[locName] = { count: 0, items: [] };
        }
        stockMap[locName].count += 1;
        stockMap[locName].items.push(item);
      });
      setTrackableStockByLocation(stockMap);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar stock rastreável'));
    }
  }, [product?.id, product?.is_trackable]);

  useEffect(() => {
    loadTrackableItems();
  }, [loadTrackableItems]);

  const currentSchema = useMemo(() => {
    if (!editData?.category) return null;
    const schemaKey = resolveProductSchemaKey(editData.category);
    return schemaKey ? schemas[schemaKey] : null;
  }, [editData?.category, schemas]);

  const viewSchema = useMemo(() => {
    const resolvedCategory = findCategory(product?.category, product?.category_id)?.base_prefix || product?.category;
    if (!resolvedCategory) return null;
    const schemaKey = resolveProductSchemaKey(resolvedCategory);
    return schemaKey ? schemas[schemaKey] : null;
  }, [findCategory, product?.category, product?.category_id, schemas]);

  const resolvedViewCategory = useMemo(() => (
    findCategory(product?.category, product?.category_id)?.base_prefix || product?.category
  ), [findCategory, product?.category, product?.category_id]);

  const resolvedViewCategoryLabel = useMemo(() => (
    findCategory(product?.category, product?.category_id)?.name || product?.category_name || product?.category || '-'
  ), [findCategory, product?.category, product?.category_id, product?.category_name]);

  const resolvedViewSubcategoryLabel = useMemo(() => (
    findSubcategory(product?.subcategory, product?.subcategory_id)?.name || product?.subcategory_name || '-'
  ), [findSubcategory, product?.subcategory, product?.subcategory_id, product?.subcategory_name]);

  const locationNameMap = useMemo(() => (
    locations.reduce((accumulator, currentLocation) => {
      if (currentLocation?.id) {
        accumulator[currentLocation.id] = currentLocation.name || currentLocation.id;
      }
      return accumulator;
    }, {})
  ), [locations]);

  const nonTrackableStockByLocation = useMemo(() => {
    if (!product || product.is_trackable) return [];

    const rawStockMap = product.stock_by_location && typeof product.stock_by_location === 'object'
      ? product.stock_by_location
      : {};

    const normalizedEntries = Object.entries(rawStockMap)
      .map(([locationId, quantity]) => ({
        locationId,
        locationName: locationNameMap[locationId] || locationId || 'Sem localização',
        quantity: Number(quantity || 0),
      }))
      .filter((entry) => entry.locationId && entry.quantity > 0)
      .sort((left, right) => right.quantity - left.quantity || left.locationName.localeCompare(right.locationName, 'pt', { sensitivity: 'base' }));

    if (normalizedEntries.length > 0) {
      return normalizedEntries;
    }

    const fallbackQuantity = Number(product.current_stock || 0);
    if (fallbackQuantity > 0 && product.location_id) {
      return [{
        locationId: product.location_id,
        locationName: locationNameMap[product.location_id] || product.location_name || product.location_id,
        quantity: fallbackQuantity,
      }];
    }

    return [];
  }, [locationNameMap, product]);

  const unallocatedNonTrackableStock = useMemo(() => {
    if (!product || product.is_trackable) return 0;
    const allocatedQuantity = nonTrackableStockByLocation.reduce((sum, entry) => sum + Number(entry.quantity || 0), 0);
    return Math.max(Number(product.current_stock || 0) - allocatedQuantity, 0);
  }, [nonTrackableStockByLocation, product]);

  const currentSubcategories = useMemo(() => (
    getSubcategoriesForCategory(editData?.category, editData?.category_id)
  ), [editData?.category, editData?.category_id, getSubcategoriesForCategory]);

  const availableLocationOptions = useMemo(() => (
    locations.filter((locationOption) => locationOption?.id && locationOption?.is_active !== false)
  ), [locations]);

  const hasUnsavedChanges = useMemo(() => (
    editMode && editData && initialSnapshot && JSON.stringify(editData) !== initialSnapshot
  ), [editData, editMode, initialSnapshot]);

  useEffect(() => {
    const handler = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    if (hasUnsavedChanges && !window.confirm('Existem alterações pendentes. Deseja sair sem guardar?')) {
      return;
    }
    navigate(-1);
  };

  const enterEditMode = () => {
    setEditData(buildEditData(product));
    setEditJustification('');
    setEditMode(true);
  };

  const cancelEdit = () => {
    if (hasUnsavedChanges && !window.confirm('Descartar alterações não guardadas?')) {
      return;
    }
    const nextEditData = buildEditData(product);
    setEditData(nextEditData);
    setInitialSnapshot(JSON.stringify(nextEditData));
    setEditJustification('');
    setEditMode(false);
    if (directEditRequested) {
      navigate(`/inventory/products/${productId}`, { replace: true, state: location.state });
    }
  };

  const handleTransfer = () => {
    const params = new URLSearchParams({
      product_id: product.id,
      sku: product.sku,
      product_name: product.name,
    });

    if (product.location_id) {
      params.set('origin_location_id', product.location_id);
    }

    navigate(`/stock-transfers?${params.toString()}`);
  };

  const openResolveLocationDialog = useCallback(() => {
    setResolveLocationId(product?.location_id || availableLocationOptions[0]?.id || '');
    setResolveLocationDialogOpen(true);
  }, [availableLocationOptions, product?.location_id]);

  const handleEditCategoryChange = useCallback((nextCategory) => {
    const nextSubcategories = getSubcategoriesForCategory(nextCategory, null);
    setEditData((prev) => ({
      ...prev,
      category: nextCategory,
      category_id: null,
      subcategory: nextSubcategories[0]?.id || '',
      invalidSubcategory: false,
      specs: {},
      pos_description_use_override: false,
      pos_description_fields: [],
    }));
  }, [getSubcategoriesForCategory]);

  const openTrackableStockManager = () => {
    setSerialNumbersDialogOpen(true);
  };

  const handleResolveUnallocatedStock = useCallback(async () => {
    if (!product?.id) return;
    if (!resolveLocationId) {
      toast.error('Seleccione a localização que deve receber o stock em falta');
      return;
    }

    setResolvingLocation(true);
    try {
      const updatedProduct = (await inventoryAPI.resolveUnallocatedStock(product.id, resolveLocationId)).data;
      setProduct(updatedProduct);
      const nextEditData = buildEditData(updatedProduct);
      setEditData(nextEditData);
      setInitialSnapshot(JSON.stringify(nextEditData));
      setResolveLocationDialogOpen(false);
      toast.success('Stock sem localização explícita corrigido com sucesso');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível atribuir a localização ao stock em falta'));
    } finally {
      setResolvingLocation(false);
    }
  }, [buildEditData, loadData, product?.id, resolveLocationId]);

  const confirmDelete = async () => {
    if (!deleteReason.trim() || deleteReason.trim().length < 5) {
      toast.error('Motivo de eliminação obrigatório (mínimo 5 caracteres)');
      return;
    }

    try {
      await inventoryAPI.deleteProduct(productId, deleteReason.trim());
      toast.success('Produto eliminado com sucesso');
      navigate('/inventory');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar produto'));
    }
  };

  const buildClassificationPayload = () => {
    const category = findCategory(editData?.category, editData?.category_id);
    const available = getSubcategoriesForCategory(category?.base_prefix || editData?.category, category?.id);
    const subcategory = available.find((item) => item.id === editData?.subcategory) || null;
    return {
      category: category?.base_prefix || editData?.category || '',
      category_id: category?.id || null,
      subcategory_id: subcategory?.id || null,
    };
  };

  const handleSave = async () => {
    if (!editJustification || editJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }

    if (editData?.invalidSubcategory && currentSubcategories.length > 0 && !editData?.subcategory) {
      toast.error('Seleccione uma subcategoria válida antes de guardar');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...editData,
        ...buildClassificationPayload(),
        technical_specs: editData.specs,
        unit_price: editData.unit_price ? parseFloat(editData.unit_price) : undefined,
        cost_price: editData.cost_price ? parseFloat(editData.cost_price) : undefined,
        reorder_level: editData.reorder_level ? parseInt(editData.reorder_level, 10) : undefined,
      };

      const updatedProduct = (await inventoryAPI.editProduct(productId, payload, editJustification.trim())).data;
      setProduct(updatedProduct);
      const nextEditData = buildEditData(updatedProduct);
      setEditData(nextEditData);
      setInitialSnapshot(JSON.stringify(nextEditData));
      setEditJustification('');
      setEditMode(false);
      toast.success('Produto actualizado com sucesso');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar alterações do produto'));
    } finally {
      setSaving(false);
    }
  };

  // Early return for empty state to prevent accessing null product properties
  if (!loading && !product) {
    return (
      <div className="space-y-4 bg-slate-50/70 p-3 md:p-4 lg:p-5" data-testid="product-detail-page">
        <EmptyStatePanel
          title="Produto não encontrado"
          description="Não foi possível carregar este produto. Verifique se o registo ainda existe ou tente novamente."
          actionLabel="Voltar ao inventário"
          onAction={() => navigate('/inventory')}
          testId="product-detail-empty-state"
        />
      </div>
    );
  }

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={Boolean(product && editData)}
      partialIssues={partialIssues}
      loadingTestId="product-detail-loading-state"
      partialIssuesTestId="product-detail-partial-issues"
      emptyState={
        <div className="space-y-6 bg-slate-50/70 p-4 md:p-6 lg:p-8" data-testid="product-detail-page">
          <EmptyStatePanel
            title="Produto não encontrado"
            description="Não foi possível carregar este produto. Verifique se o registo ainda existe ou tente novamente."
            actionLabel="Voltar ao inventário"
            onAction={() => navigate('/inventory')}
            testId="product-detail-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-4 bg-slate-50/70 p-3 md:p-4 lg:p-5" data-testid="product-detail-page">
      <div className={cn(
        'flex flex-col gap-3 rounded-[24px] border bg-white p-4 shadow-sm lg:flex-row lg:items-start lg:justify-between',
        editMode ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'
      )}>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-full border-slate-300 bg-slate-100 text-slate-700" data-testid="product-detail-mode-badge">
              {editMode ? 'Modo edição' : 'Modo visualização'}
            </Badge>
            <Badge variant="secondary" className="rounded-full bg-indigo-50 text-indigo-700" data-testid="product-detail-category-badge">{resolvedViewCategoryLabel}</Badge>
            {resolvedViewSubcategoryLabel !== '-' && <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700" data-testid="product-detail-subcategory-badge">{resolvedViewSubcategoryLabel}</Badge>}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl" data-testid="product-detail-title">{product?.name}</h1>
          <p className="text-sm text-slate-500 md:text-base" data-testid="product-detail-subtitle">
            {product?.brand || 'Sem marca'} · {product?.model || 'Sem modelo'} · SKU {product?.sku}
          </p>
          {editMode && <p className="text-sm font-medium text-amber-700">Modo de edição activo — confirme as alterações antes de guardar.</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-11 rounded-xl border-slate-300 bg-white px-4 text-slate-700 shadow-sm hover:bg-slate-50" onClick={handleBack} data-testid="product-detail-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>

          {!editMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-11 rounded-xl border-slate-300 bg-white px-4 text-slate-700 shadow-sm hover:bg-slate-50" data-testid="product-detail-actions-menu-trigger">
                  <MoreHorizontal className="mr-2 h-4 w-4" /> Mais ações
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" data-testid="product-detail-actions-menu">
                {!product?.is_trackable && unallocatedNonTrackableStock > 0 && (
                  <DropdownMenuItem onClick={openResolveLocationDialog} data-testid="product-detail-define-location-menu-button">
                    <MapPin className="h-4 w-4 text-amber-600" />
                    Corrigir stock sem localização
                  </DropdownMenuItem>
                )}
                {product?.is_trackable && (
                  <DropdownMenuItem onClick={openTrackableStockManager} data-testid="product-detail-manage-trackable-stock-button">
                    <Hash className="h-4 w-4 text-indigo-600" />
                    Ver / editar stock rastreável
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleTransfer} data-testid="product-detail-transfer-button">
                  <ArrowRightLeft className="h-4 w-4 text-blue-600" />
                  Transferir stock
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} data-testid="product-detail-delete-button">
                  <Trash2 className="h-4 w-4 text-red-600" />
                  Eliminar artigo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {editMode ? (
            <>
              <Button variant="outline" onClick={cancelEdit} data-testid="product-detail-cancel-button">
                <X className="mr-2 h-4 w-4" /> Cancelar
              </Button>
              <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={handleSave} disabled={saving} data-testid="product-detail-save-button">
                <Save className="mr-2 h-4 w-4" /> {saving ? 'A guardar...' : 'Guardar alterações'}
              </Button>
            </>
          ) : (
            <Button className="h-11 rounded-xl bg-slate-900 px-5 text-white shadow-[0_12px_30px_-12px_rgba(15,23,42,0.9)] hover:bg-slate-800" onClick={enterEditMode} data-testid="product-detail-edit-button">
              <Edit className="mr-2 h-4 w-4" /> Editar produto
            </Button>
          )}
        </div>
      </div>

      {(product?.integrity_warnings || []).length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="product-detail-integrity-warning-banner">
          <p className="font-semibold">Avisos de integridade detectados</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {(product.integrity_warnings || []).map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          {!product?.is_trackable && unallocatedNonTrackableStock > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" className="h-8 rounded-lg bg-amber-700 px-3 text-xs text-white hover:bg-amber-800" onClick={openResolveLocationDialog} data-testid="product-detail-define-location-button">
                <MapPin className="mr-1.5 h-3.5 w-3.5" /> Definir localização
              </Button>
              <Button size="sm" variant="outline" className="h-8 rounded-lg border-amber-300 bg-white px-3 text-xs text-amber-800 hover:bg-amber-100" onClick={openResolveLocationDialog} data-testid="product-detail-resolve-unallocated-stock-button">
                Corrigir stock sem localização
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[1.22fr_0.78fr] xl:items-start">
      <div className="space-y-4">
      <Card className="rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-general-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> Informação Geral</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 pt-3 md:grid-cols-3 xl:grid-cols-5">
          {editMode ? (
            <>
              <div className="space-y-1.5"><Label>Nome do produto</Label><Input value={editData.name} onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))} data-testid="product-detail-name-input" /></div>
              <div className="space-y-1.5"><Label>Marca</Label><Input value={editData.brand} onChange={(e) => setEditData((prev) => ({ ...prev, brand: e.target.value }))} data-testid="product-detail-brand-input" /></div>
              <div className="space-y-1.5"><Label>Modelo</Label><Input value={editData.model} onChange={(e) => setEditData((prev) => ({ ...prev, model: e.target.value }))} data-testid="product-detail-model-input" /></div>
              <div className="space-y-1.5"><Label>SKU</Label><Input value={editData.sku} onChange={(e) => setEditData((prev) => ({ ...prev, sku: e.target.value }))} data-testid="product-detail-sku-input" /></div>
              <div className="space-y-1.5"><Label>Part Number</Label><Input value={editData.part_number} onChange={(e) => setEditData((prev) => ({ ...prev, part_number: e.target.value }))} data-testid="product-detail-part-number-input" /></div>
              <div className="space-y-1.5"><Label>Estado</Label><Select value={editData.status} onValueChange={(value) => setEditData((prev) => ({ ...prev, status: value }))}><SelectTrigger data-testid="product-detail-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Categoria</Label><Select value={editData.category} onValueChange={handleEditCategoryChange}><SelectTrigger data-testid="product-detail-category-select"><SelectValue /></SelectTrigger><SelectContent>{safeProductCategories.map((category) => <SelectItem key={category.id} value={category.base_prefix}>{category.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Subcategoria</Label><Select value={editData.subcategory || ''} onValueChange={(value) => setEditData((prev) => ({ ...prev, subcategory: value, invalidSubcategory: false }))} disabled={currentSubcategories.length === 0}><SelectTrigger data-testid="product-detail-subcategory-select"><SelectValue placeholder={currentSubcategories.length > 0 ? 'Seleccionar subcategoria' : 'Sem subcategorias'} /></SelectTrigger><SelectContent>{currentSubcategories.map((subcategory) => <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.name}</SelectItem>)}</SelectContent></Select>{editData.invalidSubcategory && <p className="text-xs text-amber-700">A subcategoria actual já não coincide com a categoria seleccionada.</p>}</div>
              <div className="space-y-1.5"><Label>Preço de Venda</Label><Input type="number" value={editData.unit_price} onChange={(e) => setEditData((prev) => ({ ...prev, unit_price: e.target.value }))} data-testid="product-detail-unit-price-input" /></div>
              <div className="space-y-1.5"><Label>Preço de Custo</Label><Input type="number" value={editData.cost_price} onChange={(e) => setEditData((prev) => ({ ...prev, cost_price: e.target.value }))} data-testid="product-detail-cost-price-input" /></div>
              <div className="space-y-1.5"><Label>Nível de Reposição</Label><Input type="number" value={editData.reorder_level} onChange={(e) => setEditData((prev) => ({ ...prev, reorder_level: e.target.value }))} data-testid="product-detail-reorder-level-input" /></div>
              <div className="space-y-1.5 md:col-span-3 xl:col-span-5"><Label>Observações / Descrição</Label><Textarea value={editData.description} onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))} data-testid="product-detail-description-textarea" rows={2} /></div>
            </>
          ) : (
            <>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Nome</p><p className="mt-0.5 text-sm font-medium text-slate-900">{product?.name}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Marca</p><p className="mt-0.5 text-sm text-slate-900">{product?.brand || '-'}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Modelo</p><p className="mt-0.5 text-sm text-slate-900">{product?.model || '-'}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">SKU</p><p className="mt-0.5 text-sm font-mono text-slate-900">{product?.sku}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Part Number</p><p className="mt-0.5 text-sm font-mono text-slate-900">{product?.part_number || '-'}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Estado</p><p className="mt-0.5 text-sm text-slate-900">{product?.status || '-'}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Categoria</p><p className="mt-0.5 text-sm text-slate-900" data-testid="product-detail-category-value">{resolvedViewCategoryLabel}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Subcategoria</p><p className="mt-0.5 text-sm text-slate-900" data-testid="product-detail-subcategory-value">{resolvedViewSubcategoryLabel}</p></div>
              <div className="rounded-2xl bg-emerald-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-emerald-700">Preço de Venda</p><p className="mt-0.5 text-sm font-semibold text-emerald-700">{Number(product?.unit_price || 0).toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Preço de Custo</p><p className="mt-0.5 text-sm text-slate-900">{Number(product?.cost_price || 0).toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })}</p></div>
              <div className="rounded-2xl bg-indigo-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-indigo-700">Stock Actual</p><p className="mt-0.5 text-sm font-semibold text-indigo-700" data-testid="product-detail-stock-value">{product?.current_stock}</p></div>
              <div className="rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Nível de Reposição</p><p className="mt-0.5 text-sm text-slate-900">{product?.reorder_level}</p></div>
              <div className="md:col-span-3 xl:col-span-5 rounded-2xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Observações / Descrição</p><p className="mt-0.5 text-sm text-slate-900">{product?.description || '-'}</p></div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="h-full rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-pos-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="flex items-center gap-2"><Tag className="h-5 w-5" /> Descrição POS</CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          <ProductPosDescriptionConfigurator
            category={editMode ? editData.category : resolvedViewCategory}
            value={editMode ? editData : product}
            brand={editMode ? editData.brand : product?.brand}
            model={editMode ? editData.model : product?.model}
            specs={editMode ? editData.specs : (product?.technical_specs || product?.specs || {})}
            onChange={(updates) => setEditData((prev) => ({ ...prev, ...updates }))}
            readOnly={!editMode}
            testIdPrefix="product-detail"
          />
        </CardContent>
      </Card>
      </div>

      <div className="space-y-4">
      <Card className="h-full rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-technical-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Especificações Técnicas</CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          {(editMode ? currentSchema : viewSchema) ? (
            <DynamicSpecsForm
              schema={editMode ? currentSchema : viewSchema}
              specs={editMode ? (editData.specs || {}) : (product?.technical_specs || product?.specs || {})}
              onChange={(newSpecs) => setEditData((prev) => ({ ...prev, specs: newSpecs }))}
              readOnly={!editMode}
            />
          ) : (
            <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="product-detail-no-schema-message">
              Sem schema técnico configurado para esta categoria/subcategoria.
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      </div>

      {!product?.is_trackable && (
        <Card className="rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-non-trackable-stock-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Stock por localização</CardTitle>
            <p className="text-sm text-muted-foreground">Veja exactamente em que lojas/localizações este artigo está distribuído.</p>
          </CardHeader>
          <CardContent className="space-y-3 pt-3">
            <div className="grid gap-2 md:grid-cols-3">
              <div className="rounded-2xl border bg-indigo-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-indigo-700">Stock total</p>
                <p className="mt-1 text-xl font-semibold text-indigo-700" data-testid="product-detail-non-trackable-total-stock">{product?.current_stock || 0}</p>
              </div>
              <div className="rounded-2xl border bg-slate-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Localizações activas</p>
                <p className="mt-1 text-xl font-semibold" data-testid="product-detail-non-trackable-location-count">{nonTrackableStockByLocation.length}</p>
              </div>
              <div className="rounded-2xl border bg-slate-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Estado de alocação</p>
                <p className={cn('mt-1 text-sm font-semibold', unallocatedNonTrackableStock > 0 ? 'text-amber-700' : 'text-emerald-700')} data-testid="product-detail-non-trackable-allocation-state">
                  {unallocatedNonTrackableStock > 0 ? 'Pendente de localização' : 'Coerente por localização'}
                </p>
              </div>
            </div>

            {nonTrackableStockByLocation.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="product-detail-non-trackable-no-stock-message">
                Sem stock alocado por localização neste momento.
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {nonTrackableStockByLocation.map((entry) => (
                  <div key={entry.locationId} className="rounded-2xl border bg-white p-2.5" data-testid={`product-detail-location-stock-card-${entry.locationId}`}>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="h-4 w-4 text-slate-500" />
                      <span>{entry.locationName}</span>
                    </div>
                    <p className="mt-1.5 text-xl font-semibold text-indigo-600">{entry.quantity}</p>
                    <p className="text-xs text-muted-foreground">{entry.quantity === 1 ? 'unidade alocada' : 'unidades alocadas'}</p>
                  </div>
                ))}
              </div>
            )}

            {unallocatedNonTrackableStock > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="product-detail-unallocated-stock-warning">
                <p>Existem {unallocatedNonTrackableStock} unidade(s) sem localização explícita. Este artigo precisa de distribuição correcta por loja/localização antes de ser considerado coerente.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" className="h-8 rounded-lg bg-amber-700 px-3 text-xs text-white hover:bg-amber-800" onClick={openResolveLocationDialog} data-testid="product-detail-warning-define-location-button">
                    <MapPin className="mr-1.5 h-3.5 w-3.5" /> Definir localização
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 rounded-lg border-amber-300 bg-white px-3 text-xs text-amber-800 hover:bg-amber-100" onClick={openResolveLocationDialog} data-testid="product-detail-warning-resolve-location-button">
                    Corrigir stock sem localização
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {product?.is_trackable && (
        <Card className="rounded-[24px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-trackable-stock-card">
          <CardHeader className="flex flex-col gap-3 border-b border-slate-100 pb-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Hash className="h-5 w-5" /> Stock rastreável</CardTitle>
              <p className="text-sm text-muted-foreground">Visualize unidades em stock por localização e abra a gestão por serial/IMEI.</p>
            </div>
            <Button variant="outline" className="h-10 rounded-xl border-slate-300 bg-white px-3.5 text-slate-700 shadow-sm hover:bg-slate-50" onClick={openTrackableStockManager} data-testid="product-detail-open-trackable-stock-manager-button">
              <Hash className="mr-2 h-4 w-4" /> Ver / editar stock rastreável
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 pt-3">
            <div className="grid gap-2 md:grid-cols-3">
              <div className="rounded-2xl border bg-indigo-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-indigo-700">Em stock</p>
                <p className="mt-1 text-xl font-semibold text-indigo-700" data-testid="product-detail-trackable-in-stock-count">{Object.values(trackableStockByLocation).reduce((sum, entry) => sum + entry.count, 0)}</p>
              </div>
              <div className="rounded-2xl border bg-slate-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Total registado</p>
                <p className="mt-1 text-xl font-semibold" data-testid="product-detail-trackable-total-count">{trackableItems.length}</p>
              </div>
              <div className="rounded-2xl border bg-slate-50 p-2.5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Localizações activas</p>
                <p className="mt-1 text-xl font-semibold" data-testid="product-detail-trackable-location-count">{Object.keys(trackableStockByLocation).length}</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Stock por localização</p>
              {Object.keys(trackableStockByLocation).length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="product-detail-trackable-no-stock-message">
                  Sem unidades rastreáveis em stock neste momento.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {Object.entries(trackableStockByLocation).map(([locationName, entry]) => (
                    <div key={locationName} className="rounded-2xl border bg-white p-2.5" data-testid={`product-detail-trackable-location-card-${locationName}`}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <MapPin className="h-4 w-4 text-slate-500" />
                        <span>{locationName}</span>
                      </div>
                      <p className="mt-1.5 text-xl font-semibold text-indigo-600">{entry.count}</p>
                      <p className="text-xs text-muted-foreground">{entry.count === 1 ? 'unidade disponível' : 'unidades disponíveis'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Últimas unidades rastreáveis</p>
              {trackableItems.length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="product-detail-trackable-no-items-message">
                  Nenhuma unidade rastreável registada para este artigo.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {trackableItems.slice(0, 8).map((item) => (
                    <div key={item.id} className="flex flex-col gap-1.5 rounded-2xl border p-2.5 md:flex-row md:items-center md:justify-between" data-testid={`product-detail-trackable-item-${item.id}`}>
                      <div>
                        <p className="font-mono text-xs sm:text-sm">{item.imei1 || item.serial_number || 'Sem serial/IMEI'}</p>
                        <p className="text-xs text-muted-foreground">{item.condition || 'Sem condição'} · {item.location_name || item.location || 'Sem localização'}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={item.state === 'in_stock' ? 'default' : 'secondary'}>{item.state === 'in_stock' ? 'Em Stock' : item.state}</Badge>
                        <Badge variant="outline">{item.location_name || item.location || 'Sem localização'}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm" data-testid="product-detail-audit-card">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Auditoria / Metadados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Criado em</p><p className="mt-1 text-sm text-slate-900">{product?.created_at ? new Date(product.created_at).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' }) : '-'}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Última actualização</p><p className="mt-1 text-sm text-slate-900">{product?.updated_at ? new Date(product.updated_at).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' }) : '-'}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Schema</p><p className="mt-1 text-sm text-slate-900">{resolveProductSchemaKey(editMode ? editData.category : resolvedViewCategory) || 'Sem schema'}</p></div>
            <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Override POS</p><p className="mt-1 text-sm text-slate-900">{(editMode ? editData.pos_description_use_override : product?.pos_description_use_override) ? 'Sim' : 'Não'}</p></div>
          </div>

          {editMode && (
            <div className="space-y-2 border-t pt-4">
              <Label>Justificação da alteração *</Label>
              <Textarea value={editJustification} onChange={(e) => setEditJustification(e.target.value)} rows={3} data-testid="product-detail-justification-textarea" placeholder="Descreva o motivo da alteração..." />
              <p className="text-xs text-muted-foreground">Obrigatório para manter o histórico de auditoria consistente.</p>
            </div>
          )}

          <div className="space-y-2 border-t pt-4">
            <p className="text-sm font-medium">Últimos eventos</p>
            {auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem eventos de auditoria registados.</p>
            ) : auditLogs.map((log, index) => (
              <div key={log.id || `${log.product_id || 'product'}-${log.action || 'updated'}-${log.timestamp || 'sem-ts'}-${log.product_sku || 'sem-sku'}`} className="rounded-lg border bg-white p-3 text-sm shadow-sm" data-testid={`product-detail-audit-log-${log.id || index}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant="outline">{log.action || 'updated'}</Badge>
                  <span className="text-xs text-muted-foreground">{log.timestamp ? new Date(log.timestamp).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' }) : '-'}</span>
                </div>
                <p className="mt-2 text-muted-foreground">{log.justification || log.notes || 'Sem detalhe adicional.'}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar artigo</AlertDialogTitle>
            <AlertDialogDescription>
              Informe o motivo para eliminar este artigo do inventário. Esta acção fica registada na auditoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="product-detail-delete-reason">Motivo</Label>
            <Textarea
              id="product-detail-delete-reason"
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              data-testid="product-detail-delete-reason-textarea"
              placeholder="Explique por que está a eliminar este artigo..."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteReason('')} data-testid="product-detail-delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="product-detail-delete-confirm-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={serialNumbersDialogOpen} onOpenChange={(open) => {
        setSerialNumbersDialogOpen(open);
        if (!open) {
          loadTrackableItems();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-indigo-600" />
              Gestão de stock rastreável - {product?.name}
            </DialogTitle>
          </DialogHeader>
          <SerialNumbersPanel
            productId={product?.id}
            productName={product?.name}
            onClose={() => {
              setSerialNumbersDialogOpen(false);
              loadTrackableItems();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={resolveLocationDialogOpen} onOpenChange={setResolveLocationDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="product-detail-unallocated-stock-dialog">
          <DialogHeader>
            <DialogTitle>Corrigir stock sem localização</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="product-detail-unallocated-stock-dialog-summary">
              <p className="font-medium">Este ajuste não altera o stock total.</p>
              <p className="mt-1">Vai atribuir {unallocatedNonTrackableStock} unidade(s) em falta a uma localização explícita.</p>
            </div>
            <div className="space-y-2">
              <Label>Localização de destino</Label>
              <Select value={resolveLocationId} onValueChange={setResolveLocationId}>
                <SelectTrigger data-testid="product-detail-unallocated-stock-location-select">
                  <SelectValue placeholder="Seleccionar localização" />
                </SelectTrigger>
                <SelectContent>
                  {availableLocationOptions.map((locationOption) => (
                    <SelectItem key={locationOption.id} value={locationOption.id}>{locationOption.name || locationOption.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResolveLocationDialogOpen(false)} data-testid="product-detail-unallocated-stock-cancel-button">Cancelar</Button>
              <Button onClick={handleResolveUnallocatedStock} disabled={!resolveLocationId || resolvingLocation} data-testid="product-detail-unallocated-stock-confirm-button">
                {resolvingLocation ? 'A corrigir...' : 'Aplicar localização'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}